import { useState } from "react";
import {
  Dialog,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const InviteAdminModal = ({ show, onClose, onInvite, isSubmitting = false }) => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");

  const resetForm = () => {
    setFirstname("");
    setLastname("");
    setEmail("");
    setRole("viewer");
  };

  const roles = [
    {
      value: "admin",
      label: "Admin",
      description: "Full access to all features and settings",
    },
    {
      value: "manager",
      label: "Manager",
      description: "Access to all except billing and account settings",
    },
    {
      value: "viewer",
      label: "Viewer",
      description: "Read-only access to logs & usage data",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email.trim() && firstname.trim() && lastname.trim()) {
      const success = await onInvite({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim(),
        role,
      });
      if (success) {
        resetForm();
        onClose();
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <div className="pb-0 pr-16 px-6 pt-4">
        <h2 className="text-xl font-bold text-primary">Invite Admin User</h2>
        <p className="text-sm text-content mt-1">
          Send an invitation to a new admin user
        </p>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="pt-6 px-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-3">
            <TextField
              label="First Name"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              placeholder="Enter first name"
              fullWidth
              focused
              required
              variant="outlined"
            />
            <TextField
              label="Last Name"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              placeholder="Enter last name"
              fullWidth
              focused
              required
              variant="outlined"
            />
          </div>

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            fullWidth
            focused
            required
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{ fontWeight: 500, color: "text.primary", mb: 1 }}
            >
              Role
            </FormLabel>
            <RadioGroup value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((roleOption) => (
                <div key={roleOption.value} className="mb-2">
                  <FormControlLabel
                    value={roleOption.value}
                    control={
                      <Radio
                        sx={{
                          color: "grey.400",
                          "&.Mui-checked": {
                            color: "#0A2342",
                          },
                        }}
                      />
                    }
                    label={
                      <div>
                        <span className="font-medium text-gray-900">
                          {roleOption.label}
                        </span>
                        <p className="text-sm text-gray-500">
                          {roleOption.description}
                        </p>
                      </div>
                    }
                  />
                </div>
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-4">
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              bgcolor: "#0A2342",
              "&:hover": { bgcolor: "#0A2342" },
              textTransform: "uppercase",
              fontWeight: 600,
              px: 3,
            }}
          >
            {isSubmitting ? "Sending..." : "Send Inviation"}
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              borderColor: "grey.300",
              color: "text.primary",
              textTransform: "uppercase",
              fontWeight: 600,
              px: 3,
              "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default InviteAdminModal;
